/**
 * Customer-only view — no branding, no preview strip, nothing but the
 * customer's name, the two Vehicle Visuals videos, prices and Authorise
 * buttons. Public route: /demo/vehicle-visuals
 */
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, PlayCircle, ArrowRight, ExternalLink } from "lucide-react";

const DEMO = {
  customer_name: "Mr Woodman",
  items: [
    {
      id: "vv-aircon",
      name: "Air-conditioning service & re-gas",
      price: 89.0,
      video_url: "https://www.srvc.video/4DCk0",
    },
    {
      id: "vv-alignment",
      name: "Four-wheel alignment",
      price: 75.0,
      video_url: "https://www.srvc.video/j1TME",
    },
  ],
};

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

export default function VehicleVisualsDemoPage() {
  // Tell Steve the moment a forwarded prospect opens this page (?ref=phillip).
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/showcase/open`, {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ demo: "vehicle-visuals", ref, path: window.location.pathname }),
    }).catch(() => {});
  }, []);

  const [authorised, setAuthorised] = useState({});
  const [sent, setSent] = useState(false);

  const total = useMemo(
    () => DEMO.items.reduce((s, it) => s + (authorised[it.id] ? it.price : 0), 0),
    [authorised]
  );
  const anyAuthorised = Object.values(authorised).some(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <section className="max-w-2xl mx-auto px-5 pt-10 pb-6">
        <h1 className="font-display font-bold text-3xl sm:text-4xl mb-2" data-testid="vv-demo-heading">
          Hi {DEMO.customer_name}
        </h1>
        <p className="text-muted-foreground">
          Tap each video below for the explanation, then authorise the work you&rsquo;d like us to do today.
        </p>
      </section>

      <section className="max-w-2xl mx-auto px-5 space-y-5 pb-24" data-testid="vv-demo-items">
        {DEMO.items.map((it) => {
          const ok = !!authorised[it.id];
          return (
            <article
              key={it.id}
              className={`rounded-xl border overflow-hidden transition-colors ${
                ok ? "border-success/50 bg-success/[0.04]" : "border-border bg-card"
              }`}
              data-testid={`vv-demo-item-${it.id}`}
            >
              <a
                href={it.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block relative group aspect-video bg-black/95 border-b border-border overflow-hidden"
                data-testid={`vv-demo-video-${it.id}`}
              >
                <div className="absolute inset-0 grid place-items-center text-white/90 group-hover:text-white transition-colors">
                  <div className="flex flex-col items-center gap-2">
                    <PlayCircle className="w-16 h-16 drop-shadow-lg" strokeWidth={1.4} />
                    <div className="text-sm font-semibold">Watch: {it.name}</div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-1.5">
                  <ExternalLink className="w-3.5 h-3.5 text-white" />
                </div>
              </a>

              <div className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display font-bold text-lg" data-testid={`vv-demo-name-${it.id}`}>
                    {it.name}
                  </h2>
                  <div className="font-display font-bold text-2xl mt-1" data-testid={`vv-demo-price-${it.id}`}>
                    {gbp(it.price)}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={sent}
                  onClick={() => setAuthorised((a) => ({ ...a, [it.id]: !a[it.id] }))}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    ok
                      ? "bg-success text-success-foreground"
                      : "border border-success/40 text-success hover:bg-success/10"
                  }`}
                  data-testid={`vv-demo-authorise-${it.id}`}
                >
                  <CheckCircle2 className="w-4 h-4" /> {ok ? "Authorised" : "Authorise"}
                </button>
              </div>
            </article>
          );
        })}
      </section>

      <section className="border-t border-border bg-card/80 backdrop-blur fixed inset-x-0 bottom-0" data-testid="vv-demo-summary-bar">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Total authorised</div>
            <div className="font-display font-bold text-2xl" data-testid="vv-demo-total">{gbp(total)}</div>
          </div>
          <button
            type="button"
            disabled={!anyAuthorised || sent}
            onClick={() => setSent(true)}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg font-bold transition-colors ${
              sent
                ? "bg-success text-success-foreground cursor-default"
                : anyAuthorised
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
            data-testid="vv-demo-submit"
          >
            {sent
              ? <><CheckCircle2 className="w-5 h-5" /> Sent</>
              : <>Send authorisation <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </section>
    </div>
  );
}
